import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/auth.service";
import { LoginCredentials } from "../types/auth";
import {
  setCredentials,
  clearCredentials,
  setError as setReduxError,
} from "../store/authSlice";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import type { RootState } from "../store";
import { useGoogleLogin } from "@react-oauth/google";
import type { OtpResponse } from "../types/otp";
import api from "@/services/api";
import { otpService } from "../services/otp.service";
import { RoleType } from "@/types/role";
import { navIdToPath } from "@/types/auth";
import { triggerNativeLogin, triggerNativeLogout } from "../utils/nativeBridge";
import { extractErrorMessage } from "../utils/error-utils";
import {
  clearStoragePreservingTours,
  savePreferredLoginMethod,
} from "../utils/storage-utils";

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyType, setVerifyType] = useState<"email" | "mobile">("email");
  const [inputValue, setInputValue] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAuthenticated, member } = useSelector(
    (state: RootState) => state.auth
  );

  // Google OAuth login
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post("/auth/google/login", { code });
        if (data.token && data.member) {
          handleAuthSuccess(data.token, data.member);
        } else {
          setError(data.message || "Google login failed");
          showToast({
            message: data.message || "Google login failed",
            variant: "error",
          });
        }
      } catch (err: any) {
        const msg = extractErrorMessage(err, "Google login failed");
        setError(msg);
        showToast({ message: msg, variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      const msg =
        "Google OAuth error: " +
        (error?.error_description || error?.error || "Unknown error");
      setError(msg);
      showToast({ message: msg, variant: "error" });
    },
  });

  /**
   * Send OTP for login
   * @param opts { countryCode, mobile } for mobile, or nothing for email
   */
  const sendOtp = async (opts?: {
    countryCode: string;
    mobile: string;
  }): Promise<OtpResponse> => {
    setError(null);
    setLoading(true);
    try {
      let payload: any;
      if (verifyType === "mobile" && opts) {
        payload = {
          send_to: opts.mobile,
          dialCode: opts.countryCode, // send dialCode as separate field
          mode: "sms",
          type: "member_login",
        };
      } else {
        payload = {
          send_to: inputValue,
          mode: "email",
          type: "member_login",
        };
      }
      const response = await otpService.sendLoginOtp(payload);
      setOtpSent(!!response.success);
      // if (response.success) {
      //   showToast({ message: 'OTP sent', variant: 'success' });
      // }
      return response;
    } catch (err: any) {
      const msg = extractErrorMessage(err, "Failed to send OTP. Try again.");
      setError(msg);
      showToast({ message: msg, variant: "error" });
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP for login
   * @param otpArg string (optional, for direct input)
   * @param opts { dialCode, mobile } for mobile, or nothing for email
   * @param showSuccessScreen boolean (optional, to show success screen before dashboard)
   */
  const verifyOtp = async (
    otpArg?: string,
    opts?: { dialCode: string; mobile: string },
    showSuccessScreen: boolean = false
  ) => {
    setError(null);
    setLoading(true);
    try {
      let payload: any;
      const otpToUse = otpArg ?? otp;
      if (verifyType === "mobile" && opts) {
        payload = {
          send_to: opts.mobile,
          dialCode: opts.dialCode, // correct field for backend
          otp: otpToUse,
          mode: "sms",
          type: "member_login",
        };
      } else {
        payload = {
          send_to: inputValue,
          otp: otpToUse,
          mode: "email",
          type: "member_login",
        };
      }
      const response = await AuthService.loginWithOtp(payload);
      if (response.token && response.member) {
        setError(null); // Clear error before navigation
        savePreferredLoginMethod(verifyType);
        handleAuthSuccess(response.token, response.member, showSuccessScreen);
      } else {
        throw new Error(response.message || "Invalid OTP");
      }
    } catch (err: any) {
      const msg = extractErrorMessage(err, "Invalid OTP. Please try again.");
      setError(msg);
      showToast({ message: msg, variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Handles storing auth info and redirecting based on role
  const handleAuthSuccess = async (
    token: string,
    member: any,
    showSuccessScreen: boolean = false
  ) => {
    dispatch(setCredentials({ token, member }));

    const isAdmin =
      member?.roleType === "ADMIN" || member?.roleType === RoleType.Admin;

    // Send tokens directly to React Native WebView (no more code exchange)
    if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      triggerNativeLogin(token, member);
      window.location.replace("/dashboard");
      return;
    }

    if (isAdmin) {
      window.location.replace("/admin/dashboard");
      return;
    }

    // If showSuccessScreen is true, navigate to existing registration success page
    if (showSuccessScreen) {
      navigate("/register/success", {
        state: {
          firstName: member?.first_name || "",
          isLogin: true,
        },
        replace: true,
      });
      return;
    }

    // Use shared navIdToPath for navigation
    const activeNav = localStorage.getItem("maatra-active-nav-item");
    const redirectPath =
      activeNav && navIdToPath[activeNav]
        ? navIdToPath[activeNav]
        : "/dashboard";
    window.location.replace(redirectPath);
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      const response = await AuthService.login(credentials);
      handleAuthSuccess(response.token, response.member);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Login failed";
      dispatch(setReduxError(errorMessage));
      setError(errorMessage);
      showToast({ message: errorMessage, variant: "error" });
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      // Notify native app if in WebView
      triggerNativeLogout();
      // Try to call the logout API, but don't wait for it
      await AuthService.logout().catch(console.error);
      // Clear localStorage using the proper utility function (preserves tour data)
      clearStoragePreservingTours();
      // Clear Redux state (this will also clear some localStorage items again, but that's ok)
      dispatch(clearCredentials());
      navigate("/signup");
    } finally {
      setLoading(false);
    }
  };

  const clearAllStorage = () => {
    clearStoragePreservingTours();
  };

  return {
    loading,
    error,
    setError,
    verifyType,
    setVerifyType,
    inputValue,
    setInputValue,
    countryCode,
    setCountryCode,
    otpSent,
    setOtpSent,
    otp,
    setOtp,
    isAuthenticated,
    member,
    login,
    logout,
    clearAllStorage,
    googleLogin,
    sendOtp,
    verifyOtp,
  };
};
